import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, Share2, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IndicacaoService } from "@/services";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { showToast } from "@/hooks/useToast";

export const Route = createFileRoute("/app/perfil_/indicacoes")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({ to: "/auth/identify" });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
    const formatCurrency = (value: number) =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }).format(value);

  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const meuCodigoQuery = useQuery({
    queryKey: ["indicacao", "meu-codigo"],
    queryFn: IndicacaoService.getMeuCodigo,
  });

  const indicacoesQuery = useQuery({
    queryKey: ["indicacao", "minhas-indicacoes"],
    queryFn: IndicacaoService.getMinhasIndicacoes,
  });

  const codigo = meuCodigoQuery.data?.codigo;
  const referralLink = codigo
    ? `${window.location.origin}/auth/identify?ref=${codigo}`
    : "";

  const totais = useMemo(() => {
    const indicacoes = indicacoesQuery.data ?? [];
    const concluidas = indicacoes.filter((item) => item.status === "concluida");
    const valorTotal = concluidas.reduce((acc, item) => acc + item.valorCredito, 0);

    return {
      quantidade: indicacoes.length,
      concluidas: concluidas.length,
      valorTotal,
    };
  }, [indicacoesQuery.data]);

  const handleCopy = async () => {
    if (!referralLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast.success("Link de indicação copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast.error("Não foi possível copiar o link");
    }
  };

  const handleShare = async () => {
    if (!referralLink) {
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Convite JapExpress",
          text: "Use meu link de indicação no JapExpress e ganhe vantagens!",
          url: referralLink,
        });
        return;
      } catch {
        // fallback below
      }
    }

    await handleCopy();
  };

  const indicacoes = indicacoesQuery.data ?? [];

  return (
    <div className="min-h-screen bg-background pb-10">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="container mx-auto px-4 py-3 max-w-2xl flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: "/app/perfil" })}
            className="rounded-full hover:bg-black/5 -ml-2"
          >
            <ArrowLeft className="h-5 w-5 text-primary" />
          </Button>
          <h1 className="text-base font-semibold text-slate-900">Indicações</h1>
        </div>
      </header>

      <main className="container mx-auto max-w-2xl px-4 py-4 space-y-4">
        <section className="rounded-2xl border border-black/5 bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Seu código</p>
              <h2 className="text-2xl font-extrabold tracking-widest text-slate-900 mt-1">
                {codigo ?? "--------"}
              </h2>
            </div>
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Gift className="h-6 w-6 text-primary" />
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs break-all text-slate-600 mt-3">
            {referralLink || "Carregando link de indicação..."}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button variant="outline" onClick={handleCopy} disabled={!referralLink}>
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? "Copiado" : "Copiar link"}
            </Button>
            <Button onClick={handleShare} disabled={!referralLink}>
              <Share2 className="h-4 w-4 mr-2" />
              Compartilhar
            </Button>
          </div>
        </section>

        <section className="rounded-2xl border border-black/5 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Resumo</h3>
          <div className="grid grid-cols-2 gap-2 text-center">
            <ResumoCard label="Pessoas" value={totais.quantidade} />
            <ResumoCard label="Ganhos" value={formatCurrency(totais.valorTotal)} />
          </div>
        </section>

        <section className="rounded-2xl border border-black/5 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Histórico</h3>

          {indicacoesQuery.isLoading ? (
            <p className="text-sm text-slate-500">Carregando indicações...</p>
          ) : indicacoes.length === 0 ? (
            <p className="text-sm text-slate-500">Você ainda não possui indicações registradas.</p>
          ) : (
            <div className="space-y-2">
              {indicacoes.map((item) => {
                const concluida = item.status === "concluida";
                return (
                  <div
                    key={item.id}
                    className="rounded-xl border border-black/5 px-3 py-2 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.nomeIndicado}</p>
                      <p className="text-xs text-slate-500">
                        {concluida ? "Concluída em" : "Criada em"}{" "}
                        {new Date(concluida && item.completedAt ? item.completedAt : item.createdAt).toLocaleDateString("pt-BR")}
                      </p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          concluida
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {concluida ? "Concluída" : "Pendente"}
                      </span>
                      <p className="text-xs text-slate-600 mt-1">
                        {formatCurrency(item.valorCredito)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function ResumoCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-black/5 p-3 bg-slate-50">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-lg font-semibold text-slate-900">{value}</p>
    </div>
  );
}
